"use client"

import { ArrowRight, Trophy, Award, GraduationCap, CheckCircle2 } from "lucide-react"
import { motion } from "framer-motion"
import { cn } from "@/lib/utils"

const stats = [
  {
    title: "Group 1 Dominance",
    stat: "45+",
    label: "Selections",
    desc: "Final results in recent TSPCS/APPSC cycles.",
    color: "blue",
    icon: Trophy,
    svg: (
      <svg viewBox="0 0 100 100" className="w-16 h-16 absolute -bottom-1 -right-1 opacity-[0.04] group-hover:opacity-10 transition-opacity duration-500 fill-blue-950">
        <path d="M50 10 L60 35 L85 35 L65 55 L75 80 L50 65 L25 80 L35 55 L15 35 L40 35 Z" />
        <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
      </svg>
    )
  },
  {
    title: "Consistently Top Ranks",
    stat: "Rank 1",
    label: "Every Cycle",
    desc: "Single-digit ranks secured in 2021 & 2022.",
    color: "emerald",
    icon: Award,
    svg: (
      <svg viewBox="0 0 100 100" className="w-16 h-16 absolute -bottom-1 -right-1 opacity-[0.04] group-hover:opacity-10 transition-opacity duration-500 fill-emerald-950">
        <path d="M30 70 Q50 30 70 70" fill="none" stroke="currentColor" strokeWidth="6" />
        <path d="M20 80 L80 80" stroke="currentColor" strokeWidth="4" />
        <circle cx="50" cy="30" r="10" />
      </svg>
    )
  },
  {
    title: "Expert Faculty",
    stat: "15+",
    label: "Mentors",
    desc: "Ex-Bureaucrats and Subject Matter Experts.",
    color: "blue",
    icon: GraduationCap,
    svg: (
      <svg viewBox="0 0 100 100" className="w-16 h-16 absolute -bottom-1 -right-1 opacity-[0.04] group-hover:opacity-10 transition-opacity duration-500 fill-blue-950">
        <rect x="30" y="20" width="40" height="60" rx="2" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M35 35 H65 M35 50 H65 M35 65 H55" stroke="currentColor" strokeWidth="3" />
      </svg>
    )
  },
  {
    title: "High Success Rate",
    stat: "78%",
    label: "Placed",
    desc: "Interview-guided candidates secured a post.",
    color: "emerald",
    icon: CheckCircle2,
    svg: (
      <svg viewBox="0 0 100 100" className="w-16 h-16 absolute -bottom-1 -right-1 opacity-[0.04] group-hover:opacity-10 transition-opacity duration-500 fill-emerald-950">
        <path d="M20 80 L40 50 L60 65 L80 20" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
        <circle cx="80" cy="20" r="5" fill="currentColor" />
      </svg>
    )
  },
]

export default function AchievementStats() {
  return (
    <div className="relative z-20 pt-8 sm:pt-12 max-w-[1200px] mx-auto px-6 sm:px-8">
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        {stats.map((item, i) => {
          const Icon = item.icon
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="group relative bg-white border border-slate-100 p-3 sm:p-5 flex flex-col gap-2 transition-all duration-500 hover:-translate-y-1 shadow-xl hover:shadow-2xl overflow-hidden rounded-sm"
            >
              {/* Custom Institutional SVG Motif */}
              {item.svg}

              <div className="flex items-center justify-between relative z-10">
                <div className={cn(
                  "w-8 h-8 rounded-sm flex items-center justify-center transition-all duration-500 group-hover:scale-110 shadow-md",
                  item.color === "blue" ? "bg-blue-600 text-white shadow-blue-900/10" : "bg-emerald-700 text-white shadow-emerald-900/10"
                )}>
                  <Icon size={16} strokeWidth={1.5} />
                </div>
                <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0">
                  <span className="text-[8px] font-bold uppercase tracking-widest text-[#b48c36]">Verified</span>
                  <ArrowRight size={12} className="text-[#b48c36]" />
                </div>
              </div>

              <div className="space-y-0.5 relative z-10">
                <div className="flex items-baseline gap-1">
                   <h3 className="font-serif text-2xl font-bold tracking-tight text-slate-950">
                    {item.stat}
                  </h3>
                  <span className={cn(
                    "text-[7px] font-black uppercase tracking-widest",
                    item.color === "blue" ? "text-blue-600" : "text-emerald-600"
                  )}>
                    {item.label}
                  </span>
                </div>
                <p className="font-serif text-[13px] font-semibold leading-tight text-slate-800">
                  {item.title}
                </p>
              </div>

              <p className="text-[10px] text-slate-400 font-medium leading-relaxed pr-4 relative z-10">
                {item.desc}
              </p>

              {/* Decorative side accent */}
              <div className={cn(
                "absolute top-0 left-0 w-1 h-full opacity-30",
                item.color === "blue" ? "bg-blue-600" : "bg-emerald-700"
              )} />
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
